import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useProjectStore } from '@/stores/projectStore'
import { useAuthStore } from '@/stores/authStore'
import { guidanceApi } from '@/lib/api/client'
import type { SkerpaGuidanceItem } from '@ispot/shared'

// ============================================================================
// QUERY KEYS
// ============================================================================

export const guidanceKeys = {
  all: ['guidance'] as const,
  items: (projectId: string) => [...guidanceKeys.all, 'items', projectId] as const,
}

// ============================================================================
// HOOKS
// ============================================================================

export function useGuidanceItems(): {
  items: SkerpaGuidanceItem[]
  isLoading: boolean
  error: string | null
} {
  const currentProject = useProjectStore((state) => state.currentProject)
  const currentAccount = useAuthStore((state) => state.currentAccount)

  const query = useQuery({
    queryKey: guidanceKeys.items(currentProject?.id ?? ''),
    queryFn: async () => {
      if (!currentAccount?.id || !currentProject?.id) throw new Error('No project selected')
      const response = await guidanceApi.list(currentAccount.id, currentProject.id)
      return response.items
    },
    enabled: !!currentAccount?.id && !!currentProject?.id,
    staleTime: 1000 * 60,
  })

  return {
    items:     query.data ?? [],
    isLoading: query.isLoading,
    error:     query.error ? String(query.error) : null,
  }
}

export function useDismissGuidance() {
  const currentProject = useProjectStore((state) => state.currentProject)
  const currentAccount = useAuthStore((state) => state.currentAccount)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => {
      if (!currentAccount?.id || !currentProject?.id) throw new Error('No project selected')
      return guidanceApi.dismiss(currentAccount.id, currentProject.id, id)
    },
    onMutate: async (id) => {
      if (!currentProject?.id) return
      const key = guidanceKeys.items(currentProject.id)
      await queryClient.cancelQueries({ queryKey: key })
      const prev = queryClient.getQueryData<SkerpaGuidanceItem[]>(key)
      queryClient.setQueryData<SkerpaGuidanceItem[]>(key, (old) =>
        (old ?? []).filter((item) => item.id !== id)
      )
      return { prev }
    },
    onError: (_err, _id, ctx) => {
      if (!currentProject?.id || !ctx?.prev) return
      queryClient.setQueryData(guidanceKeys.items(currentProject.id), ctx.prev)
    },
    onSettled: () => {
      if (!currentProject?.id) return
      queryClient.invalidateQueries({ queryKey: guidanceKeys.items(currentProject.id) })
    },
  })
}

export function useCompleteGuidance() {
  const currentProject = useProjectStore((state) => state.currentProject)
  const currentAccount = useAuthStore((state) => state.currentAccount)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => {
      if (!currentAccount?.id || !currentProject?.id) throw new Error('No project selected')
      return guidanceApi.complete(currentAccount.id, currentProject.id, id)
    },
    onSettled: () => {
      if (!currentProject?.id) return
      queryClient.invalidateQueries({ queryKey: guidanceKeys.items(currentProject.id) })
    },
  })
}
